import React, { Fragment } from 'react'
import { Form, Formik } from 'formik'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, style } from 'app/assets/styles'
import { Link } from 'app/common/link'
import { colors } from 'shared/assets/styles'
import { Text } from 'app/common/typography'
import { Error, Password } from 'app/common/forms'
import { object, ref, string } from 'yup'
import { regex } from 'shared/utils'
import { CircleChecked, Lock } from 'app/assets/icons'
import type { NewPasswordForm } from 'shared/types'

type Props = {
    intl: IntlShape,
    updatePassword: NewPasswordForm => void,
    resetPasswordUpdateSuccess: boolean,
    resetPasswordUpdateError: string
}

const UpdateForm = (props: Props) => {
    const formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            password: string()
                .required(mandatory)
                .matches(regex.PASSWORD, {
                    message: 'form.error.regex.password',
                    excludeEmptyString: true
                }),
            confirmedPassword: string()
                .required(mandatory)
                .oneOf(
                    [ref('password'), null],
                    'form.password.update.match.error'
                )
        })
    }

    const {
        updatePassword,
        resetPasswordUpdateError,
        resetPasswordUpdateSuccess,
        intl
    } = props
    const { red } = colors
    const initialValues: any = {
        password: '',
        confirmedPassword: ''
    }

    return resetPasswordUpdateSuccess ? (
        <Fragment>
            <Paragraph center>
                <CircleChecked />
            </Paragraph>
            <Paragraph center>
                <FormattedMessage id="reset.password.update.success">
                    {(text: string) => <Text>{text}</Text>}
                </FormattedMessage>
            </Paragraph>
            <StyledConnect url={intl.formatMessage({ id: 'routes.signin' })}>
                <FormattedMessage id="reset.password.update.success.button.label" />
            </StyledConnect>
        </Fragment>
    ) : (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={async (values, actions) => {
                const data = {
                    token: '',
                    password: values.password,
                    confirmedPassword: values.confirmedPassword
                }
                try {
                    await updatePassword(data)
                } finally {
                    actions.setSubmitting(false)
                }
            }}
            validationSchema={formSubscription()}>
            {({ errors, touched, isSubmitting }) => (
                <Form>
                    <Paragraph center>
                        <Lock size={50} />
                    </Paragraph>
                    <Paragraph center>
                        <FormattedMessage id="reset.password.update.form.message">
                            {(text: string) => <Text>{text}</Text>}
                        </FormattedMessage>
                    </Paragraph>
                    <Password
                        name="password"
                        type="password"
                        labelKey="reset.password.form.update.new.title"
                        placeholderKey="reset.password.form.update.new.placeholder"
                        className={
                            errors.password && touched.password ? 'error' : ''
                        }
                    />
                    {errors.password && touched.password && (
                        <Error errorKey={errors.password} />
                    )}
                    <Password
                        name="confirmedPassword"
                        type="password"
                        labelKey="reset.password.form.update.confirm.title"
                        placeholderKey="reset.password.form.update.confirm.placeholder"
                        className={
                            errors.confirmedPassword &&
                            touched.confirmedPassword
                                ? 'error'
                                : ''
                        }
                        isPastable={false}
                    />
                    {errors.confirmedPassword && touched.confirmedPassword && (
                        <Error errorKey={errors.confirmedPassword} />
                    )}
                    <ButtonContainer>
                        <StyledButtonMedium
                            type="submit"
                            background={red}
                            disabled={isSubmitting}>
                            <FormattedMessage id="reset.password.update.form.button.label" />
                        </StyledButtonMedium>
                    </ButtonContainer>
                    {resetPasswordUpdateError && (
                        <Mentions>
                            <FormattedMessage id={resetPasswordUpdateError}>
                                {(text: string) => (
                                    <Text color={red}>{text}</Text>
                                )}
                            </FormattedMessage>
                        </Mentions>
                    )}
                </Form>
            )}
        </Formik>
    )
}

const Mentions = styled.div`
    margin-top: 25px;
    margin-bottom: 25px;
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 30px 0;
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    border: none;
`

const StyledConnect = styled(Link)`
    ${style.buttonMedium};
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    background: ${colors.yellow};
    border: none;
    margin-top: 30px;
`

const Paragraph = styled.p`
    ${props => (props.center ? 'text-align: center' : '')};
`

const UpdateFormComponent = injectIntl(UpdateForm)

export { UpdateFormComponent }
